import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const DedupeJobs = () => {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [applied, setApplied] = useState(false);

  const run = async (apply) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/dedupe-jobs", { apply });
      setReport(data.report || []);
      setApplied(apply);
      if (apply) toast.success(`Merged ${data.groups} duplicate group(s)`);
      else toast.success(`Found ${data.groups} duplicate group(s)`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-amber-300 shadow-none" data-testid="dedupe-jobs-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-slate-800">
          <AlertTriangle className="h-4 w-4" /> De-duplicate jobs
        </CardTitle>
        <p className="mt-1 text-xs text-slate-500">
          Finds jobs duplicated for the same customer + vehicle where one copy has a real price and
          another has price 0. Keeps the priced record, promotes it to the furthest pipeline stage
          reached, re-links its warranty rows and deletes the price-0 copies. Preview first — nothing
          changes until you apply.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => run(false)} disabled={busy}
                  data-testid="dedupe-preview-btn">
            {busy ? "Working…" : "Preview duplicates"}
          </Button>
          <Button onClick={() => run(true)}
                  disabled={busy || !report || report.length === 0}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="dedupe-apply-btn">
            Apply merge
          </Button>
        </div>
        {report && report.length === 0 && (
          <p className="text-sm text-emerald-700" data-testid="dedupe-none">
            No duplicate jobs found.
          </p>
        )}
        {report && report.length > 0 && (
          <div className="overflow-x-auto rounded border border-slate-200" data-testid="dedupe-report">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="p-2">Customer</th>
                  <th className="p-2">Studio</th>
                  <th className="p-2">Vehicle</th>
                  <th className="p-2">Keep (price → stage)</th>
                  <th className="p-2">Remove (price-0 copies)</th>
                </tr>
              </thead>
              <tbody>
                {report.map((g, i) => (
                  <tr key={i} className="border-t border-slate-100 align-top">
                    <td className="p-2 font-medium">{g.customer_name}</td>
                    <td className="p-2">{g.studio_name}</td>
                    <td className="p-2">{g.vehicle}</td>
                    <td className="p-2">
                      ₹{Number(g.keep.price).toLocaleString("en-IN")} · {g.keep.from_status}
                      {g.keep.to_status !== g.keep.from_status && ` → ${g.keep.to_status}`}
                    </td>
                    <td className="p-2 text-red-600">
                      {g.remove.map((r) => `${r.status} @ ₹${r.price}${r.source ? ` (${r.source})` : ""}`).join(", ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {applied && (
          <p className="text-xs text-slate-500" data-testid="dedupe-applied">
            Merge applied. Re-run Preview to confirm nothing remains.
          </p>
        )}
      </CardContent>
    </Card>
  );
};


export const ClearCollections = () => {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const COLLECTIONS = [
    "material_orders",
    "stock",
    "alerts",
    "submissions",
    "sop_tasks",
    "sop_weeks",
    "targets",
    "reminders",
  ];

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/clear-collections", { confirm });
      setResult(data);
      setConfirm("");
      toast.success("Collections emptied");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-slate-300 shadow-none" data-testid="clear-collections-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-slate-800">
          <AlertTriangle className="h-4 w-4" /> Empty operational collections
        </CardTitle>
        <p className="mt-1 text-xs text-slate-500">
          Deletes every document from exactly these collections — they are emptied, never dropped:{" "}
          <span className="font-mono">{COLLECTIONS.join(", ")}</span>. Users, studios, customers,
          jobs, warranties and all master configuration are not touched. You get a deleted count
          per collection.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="max-w-xs">
          <Label>
            Type <span className="font-mono font-semibold">CLEAR</span> to confirm
          </Label>
          <Input
            className="min-h-[44px]"
            data-testid="clear-collections-confirm-input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="CLEAR"
          />
        </div>
        <Button
          className="min-h-[44px]"
          variant="outline"
          data-testid="clear-collections-btn"
          disabled={busy || confirm !== "CLEAR"}
          onClick={run}
        >
          {busy ? "Deleting…" : "Delete documents"}
        </Button>
        {result && (
          <div
            className="overflow-x-auto rounded-md border border-brand-line"
            data-testid="clear-collections-result"
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-brand-line text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="p-2">Collection</th>
                  <th className="p-2 text-right">Deleted</th>
                  <th className="p-2 text-right">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(result.collections).map(([name, info]) => (
                  <tr key={name} className="border-b border-slate-100">
                    <td className="p-2 font-mono text-xs">{name}</td>
                    <td className="p-2 text-right tabular-nums">
                      {info ? info.deleted : "—"}
                    </td>
                    <td className="p-2 text-right tabular-nums">
                      {info ? info.remaining : "collection not present"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="p-2 text-xs text-slate-500">
              Untouched — users {result.untouched.users}, studios {result.untouched.studios},
              customers {result.untouched.customers}, jobs {result.untouched.jobs}, warranties{" "}
              {result.untouched.warranties}.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const OperationalReset = () => {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-operational", { confirm });
      setResult(data);
      setConfirm("");
      toast.success("Operational data cleared");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-amber-200 shadow-none" data-testid="operational-reset-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-amber-700">
          <AlertTriangle className="h-4 w-4" /> Reset Operational Data
        </CardTitle>
        <p className="mt-1 text-xs text-slate-500">
          Clears every transactional record so the network starts a fresh cycle: customers, leads,
          quotes and interactions; jobs and their cost records; material orders, HQ dispatch and
          receipt records; SOP submissions;
          warranties and warranty tasks; wastage; reminders and notifications; scorecard history.
          Studio stock lines and HQ warehouse stock stay configured with their reorder levels but
          go to zero on hand, and targets keep their service list with every number set to zero.
          Studios, staff logins, installer profiles and all master configuration are untouched, so
          everyone can start entering real data straight away. This cannot be undone.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="max-w-xs">
          <Label>
            Type <span className="font-mono font-semibold">RESET</span> to confirm
          </Label>
          <Input
            className="min-h-[44px]"
            data-testid="operational-reset-confirm-input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="RESET"
          />
        </div>
        <Button
          className="min-h-[44px] bg-amber-600 text-white hover:bg-amber-700"
          data-testid="operational-reset-btn"
          disabled={busy || confirm !== "RESET"}
          onClick={run}
        >
          {busy ? "Clearing…" : "Reset operational data"}
        </Button>
        {result && (
          <div
            className="rounded-md border border-brand-line bg-slate-50 p-4 text-sm text-slate-700"
            data-testid="operational-reset-result"
          >
            <p className="font-semibold text-brand-ink">
              Done — {result.studios} studio(s) and {result.users} user(s) kept,{" "}
              {result.stock_lines_zeroed} stock line(s) and {result.targets_zeroed} target(s) reset
              to zero.
            </p>
            <p className="mt-1 text-xs text-slate-500">
              {Object.keys(result.deleted || {}).length > 0
                ? `Deleted: ${Object.entries(result.deleted)
                    .map(([k, v]) => `${k} (${v})`)
                    .join(", ")}`
                : "There was no transactional data to clear."}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const FactoryReset = () => {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/factory-reset", { confirm });
      setResult(data);
      setConfirm("");
      toast.success("All operational records deleted");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-rose-200 shadow-none" data-testid="factory-reset-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base text-rose-700">
          <AlertTriangle className="h-4 w-4" /> Factory reset
        </CardTitle>
        <p className="mt-1 text-xs text-slate-500">
          Permanently deletes every operational record — studios, all users except this admin
          login, customers, leads, quotes, jobs, stock, material orders, warranties, wastage, SOP
          records, installer teams, targets, reminders and audit logs — and resets
          HQ warehouse quantities to zero. Master configuration (services, SKU catalog and prices,
          car brands and models, warranty durations, pipeline stages, regions, lead sources, roles,
          settings) is kept. This cannot be undone.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="max-w-xs">
          <Label>
            Type <span className="font-mono font-semibold">WIPE</span> to confirm
          </Label>
          <Input
            className="min-h-[44px]"
            data-testid="factory-reset-confirm-input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="WIPE"
          />
        </div>
        <Button
          className="min-h-[44px] bg-rose-600 text-white hover:bg-rose-700"
          data-testid="factory-reset-btn"
          disabled={busy || confirm !== "WIPE"}
          onClick={run}
        >
          {busy ? "Deleting…" : "Delete all operational data"}
        </Button>
        {result && (
          <div
            className="rounded-md border border-brand-line bg-slate-50 p-4 text-sm text-slate-700"
            data-testid="factory-reset-result"
          >
            <p className="font-semibold text-brand-ink">
              Reset complete — {result.remaining_users} user (this admin) remains.
            </p>
            {Object.keys(result.deleted || {}).length > 0 ? (
              <p className="mt-1 text-xs text-slate-500">
                Deleted:{" "}
                {Object.entries(result.deleted)
                  .map(([k, v]) => `${k} (${v})`)
                  .join(", ")}
              </p>
            ) : (
              <p className="mt-1 text-xs text-slate-500">
                Nothing to delete — the system was already clean.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
